import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkles, AlertTriangle, Table2, Plus, Trash2, Download, Loader2, FilePlus2 } from "lucide-react";

const CONF = { alta: "bg-emerald-100 text-emerald-700", media: "bg-amber-100 text-amber-700", bassa: "bg-slate-100 text-slate-600" };

// Prodotti Assietica suggeriti per la polizza analizzata, con scelta/override dell'operatore
export const SiClassificazione = ({ si, onSaved }) => {
  const [c, setC] = useState(si.classificazione || null);
  const [scelti, setScelti] = useState((si.prodotti_scelti || []).map((p) => p.code));
  const [catalogo, setCatalogo] = useState([]);
  const [confronto, setConfronto] = useState(false);
  const [bozze, setBozze] = useState(null);
  const preparaBozze = async () => {
    const cliente_id = si.match_anagrafica?.id;
    if (!cliente_id) { toast.error("Prima abbina il cliente in anagrafica"); return; }
    try {
      if (scelti.length) await api.put(`/smart-import/${si.id}/prodotti`, { prodotti: scelti });
      const { data } = await api.post(`/smart-import/${si.id}/bozze`, { cliente_id });
      if (!data.ok) { toast.error(data.errore); return; }
      setBozze(data.bozze);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => {
    if (!si.classificazione) api.get(`/smart-import/${si.id}/classificazione`).then(({ data }) => setC(data)).catch(() => {});
    api.get("/smart-import/config/regole").then(({ data }) => setCatalogo(data.prodotti)).catch(() => {});
  }, [si.id, si.classificazione]);
  useEffect(() => { if (c && !scelti.length) setScelti(c.proposte.map((p) => p.prodotto.code)); }, [c]); // eslint-disable-line
  if (!c) return <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-400">Classificazione in corso…</div>;
  const toggle = (code) => setScelti((s) => (s.includes(code) ? s.filter((x) => x !== code) : [...s, code]));
  const salva = async () => {
    try { await api.put(`/smart-import/${si.id}/prodotti`, { prodotti: scelti }); toast.success("Prodotti confermati"); onSaved?.(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const Card = ({ p, alt }) => (
    <label className={`flex gap-3 rounded-lg border p-3 cursor-pointer ${scelti.includes(p.prodotto.code) ? "border-cyan-500 bg-cyan-50/50" : "border-slate-200"}`} data-testid={`si-cls-${p.codice}`}>
      <input type="checkbox" checked={scelti.includes(p.prodotto.code)} onChange={() => toggle(p.prodotto.code)} className="mt-1" data-testid={`si-cls-check-${p.codice}`} />
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-slate-800">{p.prodotto.nome}</span>
          <span className="text-xs text-slate-500">({p.label})</span>
          <span className={`text-[11px] px-2 py-0.5 rounded-full ${CONF[p.confidenza]}`}>confidenza {p.confidenza} · {p.score}</span>
          {alt && <span className="text-[11px] text-slate-400">alternativa</span>}
        </div>
        <p className="text-xs text-slate-500 mt-1">Motivo: {p.motivi.join("; ") || "—"}</p>
        <p className="text-[11px] text-slate-400 mt-0.5">{p.prodotto.preventivatore}</p>
      </div>
    </label>
  );
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="si-classificazione">
      <p className="font-medium text-slate-800 mb-1 flex items-center gap-2"><Sparkles className="h-4 w-4 text-cyan-600" /> Prodotti Assietica suggeriti</p>
      {c.nota && <p className="text-xs text-slate-500 mb-2">{c.nota}</p>}
      <div className="space-y-2">
        {c.proposte.map((p) => <Card key={p.codice} p={p} />)}
        {c.alternative.map((p) => <Card key={p.codice} p={p} alt />)}
      </div>
      {c.warnings?.map((w, i) => <p key={i} className="text-xs text-amber-700 mt-2 flex gap-1"><AlertTriangle className="h-3.5 w-3.5 shrink-0" /> {w}</p>)}
      <div className="flex flex-wrap items-center gap-2 mt-3">
        <select className="text-sm border border-slate-200 rounded-md px-2 py-1.5 bg-white" value="" onChange={(e) => e.target.value && !scelti.includes(e.target.value) && setScelti([...scelti, e.target.value])} data-testid="si-cls-altro">
          <option value="">+ Altro prodotto del catalogo…</option>
          {catalogo.map((p) => <option key={p.code} value={p.code}>{p.nome}</option>)}
        </select>
        {scelti.filter((s) => ![...c.proposte, ...c.alternative].some((p) => p.prodotto.code === s)).map((s) => (
          <span key={s} className="text-xs rounded-full bg-cyan-50 border border-cyan-200 px-2 py-0.5">{catalogo.find((p) => p.code === s)?.nome || s} <button onClick={() => toggle(s)} className="ml-1 text-cyan-600">×</button></span>
        ))}
        <Button size="sm" onClick={salva} disabled={!scelti.length} className="bg-cyan-700 hover:bg-cyan-800 ml-auto" data-testid="si-cls-salva">Conferma prodotti</Button>
        <Button size="sm" variant="outline" onClick={preparaBozze} data-testid="si-bozze-btn"><FilePlus2 className="h-4 w-4 mr-1" /> Prepara una bozza per ogni prodotto</Button>
        <Button size="sm" variant="outline" onClick={() => setConfronto(true)} data-testid="si-confronto-btn"><Table2 className="h-4 w-4 mr-1" /> Confronto per il cliente</Button>
      </div>
      {bozze && (
        <div className="mt-3 rounded-lg border border-cyan-200 bg-cyan-50/40 p-3" data-testid="si-bozze-list">
          <p className="text-xs text-slate-600 mb-2">Bozze pronte (nessuna pratica salvata): aprile una per una, verificale e salvale.</p>
          {bozze.map((b) => (
            <div key={b.prodotto} className="flex items-start gap-3 py-1">
              <span className="text-sm font-medium text-slate-800 w-56 shrink-0">{b.prodotto}</span>
              <div className="flex flex-wrap gap-2 flex-1">{b.errore && <span className="text-xs text-amber-700">{b.errore}</span>}{b.links.map((l) => <a key={l.label} href={l.deep_link} target="_blank" rel="noreferrer" className="text-xs px-2.5 py-1 rounded-full bg-white border border-cyan-300 text-cyan-800 hover:bg-cyan-100" data-testid="si-bozza-link">{l.label}</a>)}</div>
            </div>
          ))}
        </div>
      )}
      {confronto && <SiConfronto si={si} onClose={() => setConfronto(false)} />}
    </div>
  );
};

const SiConfronto = ({ si, onClose }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/smart-import/${si.id}/confronto/base`).then(({ data }) => setD({ ...data, note: "" })).catch(() => onClose()); }, [si.id]); // eslint-disable-line
  const setRiga = (i, k, v) => setD((x) => ({ ...x, righe: x.righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)) }));
  const pdf = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/smart-import/${si.id}/confronto/pdf`, d, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `Confronto_${(d.prodotto || "Assietica").replace(/\W+/g, "_")}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Generazione PDF non riuscita"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="si-confronto-dialog">
        <DialogHeader><DialogTitle>Confronto per il cliente</DialogTitle>
          <DialogDescription>La colonna "Polizza attuale" arriva dal documento{d && !d.confermati ? " (dati NON ancora confermati)" : ""}. Compila tu la colonna Assietica: nulla viene inventato.</DialogDescription></DialogHeader>
        {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
          <div className="space-y-3">
            <div className="flex items-center gap-2"><span className="text-sm text-slate-600">Prodotto Assietica proposto</span>
              <Input value={d.prodotto} onChange={(e) => setD({ ...d, prodotto: e.target.value })} className="max-w-sm" data-testid="si-conf-prodotto" /></div>
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs text-slate-500"><th className="py-1 w-1/4">Voce</th><th>Polizza attuale</th><th>Proposta Assietica</th><th /></tr></thead>
              <tbody>{d.righe.map((r, i) => (
                <tr key={i}>
                  <td className="pr-2 py-1"><Input value={r.voce} onChange={(e) => setRiga(i, "voce", e.target.value)} className="h-8" data-testid={`si-conf-voce-${i}`} /></td>
                  <td className="pr-2"><Input value={r.attuale} onChange={(e) => setRiga(i, "attuale", e.target.value)} className="h-8" data-testid={`si-conf-attuale-${i}`} /></td>
                  <td className="pr-2"><Input value={r.proposta} onChange={(e) => setRiga(i, "proposta", e.target.value)} className="h-8 bg-cyan-50/40" placeholder="da compilare" data-testid={`si-conf-proposta-${i}`} /></td>
                  <td><button onClick={() => setD({ ...d, righe: d.righe.filter((_, j) => j !== i) })} className="text-slate-400 hover:text-red-500" data-testid={`si-conf-del-${i}`}><Trash2 className="h-4 w-4" /></button></td>
                </tr>))}</tbody>
            </table>
            <Button size="sm" variant="outline" onClick={() => setD({ ...d, righe: [...d.righe, { voce: "", attuale: "", proposta: "" }] })} data-testid="si-conf-add"><Plus className="h-4 w-4 mr-1" /> Aggiungi riga</Button>
            <Textarea rows={2} placeholder="Note per il cliente (facoltative)" value={d.note} onChange={(e) => setD({ ...d, note: e.target.value })} data-testid="si-conf-note" />
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={onClose}>Chiudi</Button>
          <Button onClick={pdf} disabled={busy || !d} className="bg-cyan-700 hover:bg-cyan-800" data-testid="si-conf-pdf">{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />} Scarica PDF</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
